import { LOCK_INITIAL_DURATION } from "@/config";
import { LOCK_EVENTS } from "@/events";
import { useEventListener, useTokenForm, useDialogPortal, useAnnualProjectedEarnings } from "@/hooks";
import { useController } from "react-hook-form";
import { LOCK_DURATION_KEY, type LockTokenId, TOKEN_IDS } from "@/types";

interface UseLockFormProps {
    initialAmount?: number;
    initialDuration?: number;
    initialTokenId?: LockTokenId;
}

export function useLockForm({
    initialAmount,
    initialDuration = LOCK_INITIAL_DURATION,
    initialTokenId,
}: UseLockFormProps = {}) {
    const { openLockTransactionDialog } = useDialogPortal();

    const {
        form,
        amount: lockAmount,
        amountError: lockAmountError,
        selectedTokenId,
        selectTokenId,
        setMaxAmount,
        resetAmount,
        isMaxDisabled,
        isAmountOverBalance,
    } = useTokenForm({
        defaultTokenId: initialTokenId ?? TOKEN_IDS.USDC,
        initialAmount,
    });

    const {
        field: duration,
        fieldState: { error: durationError },
    } = useController({
        name: LOCK_DURATION_KEY,
        control: form.control,
        defaultValue: initialDuration,
    });

    useEventListener(LOCK_EVENTS.SUCCESS, resetAmount);

    const submitLock = form.handleSubmit(() => {
        const payload = {
            lockAmount: Number(lockAmount.value),
            duration: Number(duration.value),
            lockTokenId: selectedTokenId,
        };
        openLockTransactionDialog(payload);
    });

    const { calculateProjectedEarnings, apy, isAPYLoading } = useAnnualProjectedEarnings(
        duration.value,
        Number(lockAmount.value),
    );
    const projectedEarnings = calculateProjectedEarnings(lockAmount.value);

    return {
        lockAmount,
        lockAmountError,
        duration,
        durationError,
        selectedTokenId,
        apy,
        isAPYLoading,
        projectedEarnings,
        submitLock,
        selectTokenId,
        setMaxAmount,
        isMaxDisabled,
        isAmountOverBalance,
    };
}
